import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { NgForOf, NgIf } from '@angular/common';

export interface SplitButtonAction {
  label: string;
  value: string;
  variant?: 'primary' | 'secondary' | 'tertiary' | 'success' | 'warning' | 'danger' | 'info' | 'outline';
  faIcon?: string;
  dropdownFaIcon?: string;
  dropdownVariant?: 'primary' | 'secondary' | 'tertiary' | 'success' | 'warning' | 'danger' | 'info' | 'outline';
  dropdownLabel?: string;
  disabled?: boolean;
  default?: boolean;
}

@Component({
  selector: 'app-split-button',
  standalone: true,
  imports: [NgIf, NgForOf],
  templateUrl: './split-button.component.html',
  styleUrls: ['./split-button.component.scss'],
})
export class SplitButtonComponent {
  @Input() type: 'button' | 'submit' = 'button';
  @Input() size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'md';
  @Input() width: 'wrap' | 'fill' = 'wrap';
  @Input() margin: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'none';
  @Input() padding: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'md';
  @Input() disabled: boolean = false;
  @Input() visible: boolean = true;
  @Input() isBusy: boolean = false;
  @Input() className: string = '';
  @Input() iconPosition: 'left' | 'right' = 'left';
  @Input() textAlign: 'left' | 'center' | 'right' = 'center';
  @Input() title: string = '';
  @Input() actions: SplitButtonAction[] = [];

  @Output() actionSelected = new EventEmitter<string>();

  protected dropdownOpen: boolean = false;

  constructor(private elementRef: ElementRef) {}

  @HostListener('document:click', ['$event.target'])
  onClickOutside(target: HTMLElement): void {
    if (!this.elementRef.nativeElement.contains(target)) {
      this.dropdownOpen = false;
    }
  }

  get defaultAction(): SplitButtonAction | null {
    return this.actions.find(a => a.default) ?? this.actions[0] ?? null;
  }

  get dropdownActions(): SplitButtonAction[] {
    const defaultAction = this.defaultAction;
    return this.actions.filter(a => a !== defaultAction);
  }

  protected onMainClick(): void {
    if (this.disabled) return;
    if (this.defaultAction) {
      this.actionSelected.emit(this.defaultAction.value);
    }
  }

  protected onToggleDropdown(event: Event): void {
    event.stopPropagation();
    if (this.disabled) return;
    this.dropdownOpen = !this.dropdownOpen;
  }

  protected onActionClick(action: SplitButtonAction, event: Event): void {
    event.stopPropagation();
    if (action.disabled) return;
    this.dropdownOpen = false;
    this.actionSelected.emit(action.value);
  }

  protected mainClassNames(): string {
    const variant = this.defaultAction?.variant ?? 'primary';
    const classes = [
      'button',
      'button-' + variant,
      'size-' + this.size,
      'margin-' + this.margin,
      'padding-' + this.padding,
      'split-main',
    ];

    if (this.className) classes.push(this.className);

    return classes.join(' ');
  }

  protected toggleClassNames(): string {
    const variant = this.defaultAction?.variant ?? 'primary';
    const classes = [
      'button',
      'button-' + variant,
      'size-' + this.size,
      'padding-' + this.padding,
      'split-toggle',
    ];

    return classes.join(' ');
  }
}
